// src/components/CookieConsentModal.jsx
import React, { useEffect, useState } from "react";
import { getCookie, setCookie, eraseCookie } from "../Utils/Cookies";

const GA_ID = String(
  process.env.REACT_APP_GA4_MEASUREMENT_ID || "",
).trim();

export default function CookieConsentModal({
  mode = "b2b",                         // "b2b" | "b2c"
  policyHref = "/b2b/隱私權",
  version = 1,
  triggerEventName = "app:openCookieSettings",
  autoShow = false,                     // only set true on the landing page
  cookieDays = 365,
}) {
  const KEY = `seho_cc_v${version}_${mode}`;
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);

  // optional categories
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  const fonts = "LINE Sans TW";

  // read existing cookie
  useEffect(() => {
    const raw = getCookie(KEY);
    const hasConsent = !!raw;
    if (hasConsent) {
      try {
        const parsed = JSON.parse(raw);
        setAnalytics(!!parsed.analytics);
        setMarketing(!!parsed.marketing);
      } catch {}
    }
    // Only auto-open on first visit to landing pages when no consent cookie
    if (autoShow && !hasConsent) {
      setSettings(false);
      setOpen(true);
    }
  }, [KEY, autoShow]);

  // allow opening from anywhere
  useEffect(() => {
    const onOpen = () => {
      setSettings(true);
      setOpen(true);
    };
    window.addEventListener(triggerEventName, onOpen);
    return () => window.removeEventListener(triggerEventName, onOpen);
  }, [triggerEventName]);

  // persist + apply category side-effects (like GA disable cookie)
  const applyConsent = (accepted, opts = null) => {
    const a = opts?.analytics ?? analytics;
    const m = opts?.marketing ?? marketing;

    const payload = JSON.stringify({
      accepted: !!accepted,
      ts: Date.now(),
      mode,
      version,
      necessary: true,
      analytics: !!a,
      marketing: !!m,
    });

    setCookie(KEY, payload, { days: cookieDays });
    setCookie("seho_cc_analytics", (a ? "1" : "0"), { days: cookieDays });
    setCookie("seho_cc_marketing", (m ? "1" : "0"), { days: cookieDays });

    // GA kill switch pattern (optional)
    if (GA_ID) {
      const flag = `ga-disable-${GA_ID}`;
      // Persist the disable flag as a cookie so GA won't initialize accidentally
      if (!a) {
        setCookie(flag, "true", { days: cookieDays });
        window[flag] = true;
      } else {
        eraseCookie(flag);
        window[flag] = false;
      }
    }

    setOpen(false);
    setSettings(false);

    // Let the app know consent changed (so gates can (un)load scripts)
    window.dispatchEvent(new CustomEvent("seho:consentChanged", { detail: { analytics: a, marketing: m } }));
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 30000,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(1px)",
      }}
    >
      <div
        style={{
          width: "min(680px, 92vw)",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 900, letterSpacing: 6, fontFamily: fonts }}>SEHO</div>
        </div>

        {!settings ? (
          <>
            <h2 id="cookie-title" style={{ margin: "8px 0 12px", fontSize: 18, fontFamily: fonts, fontWeight: 700 }}>
              我們使用 Cookies 以提升網站體驗
            </h2>
            <p style={{ lineHeight: 1.6, color: "#444", margin: "0 0 16px", fontFamily: fonts, fontWeight: 100 }}>
              我們使用 Cookies 改善網站的使用體驗，包含網站流量分析、使用行為統計，以及在您同意時使用行銷與個人化相關功能。您可以選擇全部接受、全部拒絕，或進入「Cookies 設定」自行調整類別。欲瞭解詳細內容，請查看
              <a href={policyHref} style={{ marginLeft: 4, textDecoration: "underline", fontFamily: fonts, fontWeight: 400 }}>
                隱私權 / Cookies 政策
              </a>
              。您可隨時在網站頁尾進入「Cookies 設定」變更選項。
            </p>

            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => applyConsent(true, { analytics: true, marketing: true })}
                style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid #111", background: "#111", color: "#fff", fontWeight: 700, fontFamily: fonts }}
              >
                全部接受
              </button>
              <button
                onClick={() => applyConsent(false, { analytics: false, marketing: false })}
                style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid #bbb", background: "#fff", color: "#111", fontWeight: 700, fontFamily: fonts }}
              >
                全部拒絕
              </button>
              <button
                onClick={() => setSettings(true)}
                style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid #111", background: "#fff", color: "#111", fontWeight: 700, fontFamily: fonts }}
              >
                Cookies 設定
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ margin: "8px 0 12px", fontSize: 18 }}>Cookies 設定</h2>
            <div style={{ display: "grid", gap: 16, marginBottom: 16 }}>
              <label style={{ padding: "12px 14px", border: "1px solid #ddd", borderRadius: 10, display: "flex", alignItems: "center", gap: 12, opacity: 0.8 }} title="必要 Cookie 無法停用">
                <input type="checkbox" checked readOnly />
                <div>
                  <div style={{ fontWeight: 600 }}>必要 (不可停用)</div>
                  <div style={{ fontSize: 13, color: "#555", fontFamily: fonts, fontWeight: 100 }}>基本網站功能與安全性</div>
                </div>
              </label>

              <label style={{ padding: "12px 14px", border: "1px solid #ddd", borderRadius: 10, display: "flex", alignItems: "center", gap: 12 }}>
                <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />
                <div>
                  <div style={{ fontWeight: 700, fontFamily: fonts }}>分析</div>
                  <div style={{ fontSize: 13, color: "#555", fontFamily: fonts, fontWeight: 400 }}>了解流量與使用行為以優化體驗</div>
                </div>
              </label>

              <label style={{ padding: "12px 14px", border: "1px solid #ddd", borderRadius: 10, display: "flex", alignItems: "center", gap: 12 }}>
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
                <div>
                  <div style={{ fontWeight: 700, fontFamily: fonts }}>行銷</div>
                  <div style={{ fontSize: 13, color: "#555", fontFamily: fonts, fontWeight: 700 }}>顯示個人化廣告與追蹤轉換</div>
                </div>
              </label>
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => applyConsent(true, { analytics, marketing })}
                style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid #111", background: "#111", color: "#fff", fontWeight: 700, fontFamily: fonts }}
              >
                儲存
              </button>
              <button
                onClick={() => setSettings(false)}
                style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid #bbb", background: "#fff", color: "#111", fontWeight: 700, fontFamily: fonts }}
              >
                返回
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


